/**
 * The full catalogue of broadcast overlay modes, grouped for the operator.
 *
 * Single source of truth — both the Classic overlay tab and the Control
 * Room `OverlayBoard` read from here. The `id`s are the contract with the
 * score overlay graphic (`overlay.show`/`overlay.hide` carry them verbatim),
 * so they MUST NOT change without updating the overlay.
 */
export type OverlayMode = {
  id: string;
  label: string;
  /** Which team the mode targets, when it has a side. Lets the UI lay home
   *  and away variants out as a pair instead of a flat list. */
  side?: "home" | "away";
  /** `lineup` — the tile sends `lineup.show/hide`, not `overlay.show` (GRID is a
   *  separate scene, driven by the `lineup` replicant). Otherwise a regular overlay. */
  kind?: "lineup";
};

export type OverlayGroup = {
  name: string;
  modes: OverlayMode[];
  /** When the column count is fixed (Tarkib: H row / A row). Otherwise
   *  the tiles are laid out automatically according to the space. */
  cols?: number;
  /** Shown only in the Classic tab — in Control Room these banners are sent
   *  by the Quick event chains, duplicate tiles are noise. */
  classicOnly?: boolean;
};

export const OVERLAY_GROUPS: OverlayGroup[] = [
  {
    // SKORE `table` radio group — the main view on the scene, mutually exclusive
    // (scoreboard is also an ordinary member of this group, not a separate block).
    name: "Sahna",
    modes: [
      { id: "scoreboard", label: "Scoreboard" },
      { id: "big", label: "Big tablo" },
      { id: "matchid", label: "Match ID" },
      { id: "stadium", label: "Stadium" },
      { id: "stats", label: "Statistika" },
      { id: "weather", label: "Ob-havo" },
      { id: "custom", label: "Custom" },
      { id: "tournament_1_8", label: "Jadval 1-8" },
      { id: "tournament_9_16", label: "Jadval 9-16" },
      { id: "player_compare_pre", label: "O'yinchilar · pre" },
      { id: "player_compare_ht", label: "O'yinchilar · HT" },
      { id: "player_stat", label: "O'yinchi stat" },
    ],
  },
  {
    // The lineup goes on air per team, in this order:
    // Starters → Grid (tactical board) → Substitutes. Each row is one team.
    name: "Tarkib",
    cols: 3,
    modes: [
      { id: "roster_home", label: "Asosiy", side: "home" },
      { id: "grid_home", label: "Grid", side: "home", kind: "lineup" },
      { id: "reserve_home", label: "Zaxira", side: "home" },
      { id: "roster_away", label: "Asosiy", side: "away" },
      { id: "grid_away", label: "Grid", side: "away", kind: "lineup" },
      { id: "reserve_away", label: "Zaxira", side: "away" },
    ],
  },
  {
    // SKORE `extra_little_mode` — small panels under the scoreboard.
    name: "Little titles",
    modes: [
      { id: "little_sponsor", label: "Sponsor 1" },
      { id: "little_sponsor_2", label: "Sponsor 2" },
      { id: "little_extra_time", label: "Extra time" },
      { id: "little_names_universal", label: "Universal" },
      { id: "little_stats_passes", label: "Passes" },
      { id: "little_stats_ball_control", label: "Ball control" },
      { id: "little_stats_tackles", label: "Tackles" },
      { id: "little_stats_shots", label: "Shots" },
      { id: "little_stats_key_passes", label: "Key passes" },
      { id: "little_stats_saves", label: "Saves" },
      { id: "little_stats_corners", label: "Corners" },
      { id: "little_stats_touches", label: "Touches" },
      { id: "little_stats_fouls", label: "Fouls" },
      { id: "little_stats_offsides", label: "Offsides" },
      { id: "little_stats_xg", label: "xG" },
      { id: "little_stats_cards_yellow", label: "Yellows" },
      { id: "little_stats_cards_red", label: "Reds" },
    ],
  },
  {
    // SKORE `extra_titre_lower` — lower titres (referees, coaches).
    name: "Titre",
    modes: [
      { id: "little_names_ref", label: "Referee" },
      { id: "little_names_var", label: "VAR ref" },
      { id: "little_names_commentator", label: "Commentator" },
      { id: "coach_id_home", label: "Coach", side: "home" },
      { id: "coach_id_away", label: "Coach", side: "away" },
    ],
  },
  {
    // Sent by the Quick event chains — no tile is needed in Control Room,
    // it stays in the Classic tab as a manual fallback.
    name: "Event banners (qo'lda)",
    classicOnly: true,
    modes: [
      { id: "little_event_goal_home", label: "Goal", side: "home" },
      { id: "little_event_goal_away", label: "Goal", side: "away" },
      { id: "little_event_yellow_home", label: "Yellow", side: "home" },
      { id: "little_event_yellow_away", label: "Yellow", side: "away" },
      { id: "little_event_red_home", label: "Red", side: "home" },
      { id: "little_event_red_away", label: "Red", side: "away" },
      { id: "little_event_swap_home", label: "Sub", side: "home" },
      { id: "little_event_swap_away", label: "Sub", side: "away" },
      { id: "little_event_scorer_home", label: "Scorer", side: "home" },
      { id: "little_event_scorer_away", label: "Scorer", side: "away" },
    ],
  },
  {
    name: "Player cards (qo'lda)",
    classicOnly: true,
    modes: [
      { id: "little_card_goal_home", label: "Card goal", side: "home" },
      { id: "little_card_goal_away", label: "Card goal", side: "away" },
      { id: "little_card_yellow_home", label: "Card yellow", side: "home" },
      { id: "little_card_yellow_away", label: "Card yellow", side: "away" },
      { id: "little_card_red_home", label: "Card red", side: "home" },
      { id: "little_card_red_away", label: "Card red", side: "away" },
      { id: "little_card_swap_home", label: "Card sub", side: "home" },
      { id: "little_card_swap_away", label: "Card sub", side: "away" },
    ],
  },
];

// The mode classification (titre/little/transient) now lives in @livcg/schema —
// the auto-sponsor/extra-time in the extension reads these very same predicates too.
export { isTableauSubMode, TITRE_OVERLAY_MODES as TITRE_MODE_IDS } from "@livcg/schema";

/** Human label for a mode id — used by ON AIR badges that only have the id. */
export function overlayModeLabel(id: string): string {
  for (const group of OVERLAY_GROUPS) {
    for (const mode of group.modes) {
      if (mode.id === id) {
        return mode.side ? `${mode.label} · ${mode.side}` : mode.label;
      }
    }
  }
  return id;
}
